/**
 * 訂單排序工具函數
 * 專門處理合併結帳訂單的特殊排序需求
 */

/**
 * 合併結帳訂單的特殊排序函數
 * 排序規則：A主單 → A子單們 → B主單 → B子單們
 * 只有合併結帳的訂單需要特殊排序，其他訂單維持時間倒序
 * 
 * @param {Array} orders - 訂單陣列
 * @returns {Array} 排序後的訂單陣列
 */
export function sortOrdersWithMergedPriority(orders) {
    if (!orders || !Array.isArray(orders)) {
        return []
    }

    // 複製陣列避免修改原始資料
    const ordersCopy = [...orders]

    return ordersCopy.sort((a, b) => {
        // 檢查是否為合併結帳相關訂單
        const aIsMergedMainOrder = !a.isChildOrder && a.orderContent._combined_order_sheets && a.orderContent._combined_order_sheets.length > 0
        const bIsMergedMainOrder = !b.isChildOrder && b.orderContent._combined_order_sheets && b.orderContent._combined_order_sheets.length > 0
        const aIsChildOrder = a.isChildOrder
        const bIsChildOrder = b.isChildOrder

        // 如果都不是合併結帳相關訂單，按原本時間倒序
        if (!aIsMergedMainOrder && !bIsMergedMainOrder && !aIsChildOrder && !bIsChildOrder) {
            return new Date(b.orderDateTime) - new Date(a.orderDateTime)
        }

        // 合併結帳訂單的特殊排序邏輯

        // 情況1: 兩個都是合併結帳的主單
        if (aIsMergedMainOrder && bIsMergedMainOrder) {
            // 按時間倒序排列
            return new Date(b.orderDateTime) - new Date(a.orderDateTime)
        }

        // 情況2: a 是主單，b 是子單
        if (aIsMergedMainOrder && bIsChildOrder) {
            // 檢查 b 是否是 a 的子單
            const aOrderId = a.orderContent._orderContent?.id || a.orderNumber.split(':')[1]
            const bHostOrderId = b.orderContent._orderContent?.hostOrderSheetID

            if (bHostOrderId === aOrderId) {
                return -1 // a 主單排在其子單 b 前面
            }
            // 如果不是同一組，按時間排序
            return new Date(b.orderDateTime) - new Date(a.orderDateTime)
        }

        // 情況3: a 是子單，b 是主單
        if (aIsChildOrder && bIsMergedMainOrder) {
            // 檢查 a 是否是 b 的子單
            const bOrderId = b.orderContent._orderContent?.id || b.orderNumber.split(':')[1]
            const aHostOrderId = a.orderContent._orderContent?.hostOrderSheetID

            if (aHostOrderId === bOrderId) {
                return 1 // b 主單排在其子單 a 前面
            }
            return new Date(b.orderDateTime) - new Date(a.orderDateTime)
        }

        // 情況4: 兩個都是子單
        if (aIsChildOrder && bIsChildOrder) {
            const aHostOrderId = a.orderContent._orderContent?.hostOrderSheetID
            const bHostOrderId = b.orderContent._orderContent?.hostOrderSheetID

            if (aHostOrderId === bHostOrderId) {
                // 同一個主單的子單，按時間排序
                return new Date(b.orderDateTime) - new Date(a.orderDateTime)
            }
            // 不同主單的子單，需要根據其主單的時間來排序
            // 這裡先按時間排序，實際可能需要更複雜的邏輯
            return new Date(b.orderDateTime) - new Date(a.orderDateTime)
        }

        // 情況5: 一般訂單與合併結帳訂單混合
        if (!aIsMergedMainOrder && !aIsChildOrder && (bIsMergedMainOrder || bIsChildOrder)) {
            // 一般訂單與合併結帳訂單按時間排序
            return new Date(b.orderDateTime) - new Date(a.orderDateTime)
        }

        if ((aIsMergedMainOrder || aIsChildOrder) && !bIsMergedMainOrder && !bIsChildOrder) {
            // 合併結帳訂單與一般訂單按時間排序
            return new Date(b.orderDateTime) - new Date(a.orderDateTime)
        }

        // 其他情況按時間倒序
        return new Date(b.orderDateTime) - new Date(a.orderDateTime)
    })
}

/**
 * 將排序後的訂單按日期分組
 * 
 * @param {Array} sortedOrders - 已排序的訂單陣列
 * @returns {Object} 按日期分組的訂單物件
 */
export function groupOrdersByDate(sortedOrders) {
    if (!sortedOrders || !Array.isArray(sortedOrders)) {
        return {}
    }

    return sortedOrders.reduce((acc, cur) => {
        if (!acc[cur.orderDate]) {
            acc[cur.orderDate] = {
                date: cur.orderDate,
                list: [cur]
            }
        } else {
            acc[cur.orderDate].list.push(cur)
        }
        return acc
    }, {})
}

/**
 * 完整的訂單排序與分組處理函數
 * 結合排序和分組功能
 * 
 * @param {Array} orders - 原始訂單陣列
 * @returns {Object} 排序並分組後的訂單物件
 */
export function processOrdersWithMergedPriority(orders) {
    const sortedOrders = sortOrdersWithMergedPriority(orders)
    return groupOrdersByDate(sortedOrders)
}